import { OrderStepStatus } from '@features/siteStatusV2/types/order/enums';
import theme, { ThemeVariants } from '@theme';
import React from 'react';
import { Accordion } from 'react-bootstrap';
import styled from 'styled-components';

import { DisplayFieldValue } from '../display-form';

interface AccordionSection {
  eventKey: string;
  header: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  statusKey: OrderStepStatus | undefined;
  status: string;
  colorStatus: ThemeVariants;
}

const AccordionSection: React.FC<AccordionSection> = ({
  eventKey,
  header,
  icon,
  children,
  statusKey,
  status,
  colorStatus
}) => (
  <AccordionItem eventKey={eventKey}>
    <div className="d-flex align-items-center">
      <AccordionHeader>
        <div className="title">{header}</div>
        <DisplayFieldValue label={status} badge={{ color: colorStatus, full: true }} />
      </AccordionHeader>
    </div>
    <RoundedStatusIcon status={statusKey}>{icon}</RoundedStatusIcon>
    <Line />
    <AccordionBody>{children}</AccordionBody>
  </AccordionItem>
);

const AccordionItem = styled(Accordion.Item)`
  border: none;
  background: ${theme.palette.grey[50]};
  margin-top: 0.75rem;
  position: relative;

  .accordion-header {
    width: 100%;
  }
`;

const AccordionHeader = styled(Accordion.Header)`
  .accordion-button {
    font-size: ${theme.fonts.size.xs};
    font-weight: ${theme.fonts.weight.medium};
    color: ${theme.palette.grey[700]};
    border: none;
    border-radius: 0;
    box-shadow: none;
    background-color: transparent;
    padding: 0.625rem 0.75rem;
    position: relative;
    width: 100%;

    &::after {
      background-size: 0.75rem;
      position: absolute;
      right: 0.625rem;
      top: 0.675rem;
      width: 0.75rem;
      height: 0.625rem;
      filter: brightness(0);
    }

    .title {
      margin-right: 0.75rem;
    }
  }
`;

const AccordionBody = styled(Accordion.Body)`
  border: none;
  padding: 0.3125rem 0.75rem;
`;

const RoundedStatusIcon = styled.div<{ status: OrderStepStatus | undefined }>`
  width: 1.875rem;
  height: 1.875rem;
  position: absolute;
  left: 0;
  top: 0.1875rem;
  transform: translateX(-100%);
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ status, theme }) =>
    status === OrderStepStatus.COMING_SOON ? theme.palette.grey[300] : theme.palette.blue[500]};
  border-radius: 100%;
  z-index: 2;
  border: 0.3125rem solid white;
  padding: 0.3125rem;

  &::after {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    position: absolute;
    bottom: -4px;
    right: -3px;
    z-index: 3;
    padding: 2px;
    border-radius: 100%;
    background: ${({ status, theme }) =>
      status === OrderStepStatus.COMING_SOON ? theme.palette.grey[300] : theme.palette.green[500]};
    border: 2px solid white;
  }

  .icon {
    fill: white;
  }
`;

const Line = styled.div`
  background: ${theme.palette.grey[100]};
  width: 0.125rem;
  position: absolute;
  top: 0;
  bottom: 0;
  left: -1rem;
  z-index: 1;
`;

export default AccordionSection;
